import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { api, setToken } from './api';

/**
 * Persona shape for the MDA staff console only — the public site (datasets,
 * budget, FOI, notices, integrity) never reads this and needs no sign-in at
 * all (see src/App.tsx). Unlike GovFin's four-workspace persona, OpenGov has
 * exactly one staff console, so there's no `workspace` union here — just the
 * module-scoped role codes (`DATA_STEWARD:<mda>`, `FOI_OFFICER`) each page
 * checks directly.
 */
export interface Persona {
  id: string;
  name: string;
  title: string;
  org: string;
  /** Primary MDA this staffer belongs to, if any — prefills ownerMda/mda form fields. */
  mdaCode?: string;
  roles: string[];
  trustTier?: string;
}

export interface AuthConfig {
  oneCitizen: boolean;
  devLogin: boolean;
}

interface AuthState {
  persona: Persona | null;
  personas: Persona[];
  config: AuthConfig;
  loading: boolean;
  login: (personaId: string) => Promise<void>;
  /** Store a session issued by the gateway (One Citizen sign-in). */
  completeLogin: (token: string, persona: Persona) => void;
  logout: () => void;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [persona, setPersona] = useState<Persona | null>(() => {
    const raw = localStorage.getItem('opengov.persona');
    return raw ? (JSON.parse(raw) as Persona) : null;
  });
  const [personas, setPersonas] = useState<Persona[]>([]);
  const [config, setConfig] = useState<AuthConfig>({ oneCitizen: false, devLogin: true });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get<{ items: Persona[] }>('/auth/personas').catch(() => ({ items: [] as Persona[] })),
      api.get<AuthConfig>('/auth/config').catch(() => ({ oneCitizen: false, devLogin: true })),
    ])
      .then(([p, c]) => {
        setPersonas(p.items);
        setConfig(c);
      })
      .finally(() => setLoading(false));
  }, []);

  const completeLogin = useCallback((token: string, p: Persona) => {
    setToken(token);
    localStorage.setItem('opengov.persona', JSON.stringify(p));
    setPersona(p);
  }, []);

  const login = useCallback(
    async (personaId: string) => {
      const r = await api.post<{ token: string; persona: Persona }>('/auth/dev-login', { personaId });
      completeLogin(r.token, r.persona);
    },
    [completeLogin],
  );

  const logout = useCallback(() => {
    setToken(null);
    localStorage.removeItem('opengov.persona');
    setPersona(null);
  }, []);

  const value = useMemo(
    () => ({ persona, personas, config, loading, login, completeLogin, logout }),
    [persona, personas, config, loading, login, completeLogin, logout],
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth outside AuthProvider');
  return ctx;
}
